<template>
  <section class="container-fluid px-3 bg-dark d-flex justify-content-center pt-5 mt-3 product-details-section">
    <!-- Loading State - Solo se muestra mientras se carga el producto principal -->
    <div v-if="isLoadingProduct" class="container row col-12 d-flex justify-content-center mt-5 py-5">
      <div class="col-lg-6 px-0 pe-lg-4 mt-5">
        <div class="skeleton-image" style="height: 400px; border-radius: 15px;"></div>
      </div>
      <div class="col-lg-5 my-4 my-lg-0 px-0 mt-5 pt-5">
        <div class="skeleton-content">
          <div class="skeleton-title mb-3" style="height: 40px; width: 70%;"></div>
          <div class="skeleton-tags d-flex gap-2 mb-4">
            <div class="skeleton-tag" style="height: 30px; width: 100px;"></div>
            <div class="skeleton-tag" style="height: 30px; width: 80px;"></div>
          </div>
          <div class="skeleton-description mb-4" style="height: 120px;"></div>
          
          <!-- Skeleton for variants -->
          <div class="skeleton-variants mb-4" style="height: 100px;"></div>
          
          <div class="skeleton-price mb-4" style="height: 50px; width: 50%;"></div>
          <div class="d-flex gap-3">
            <div class="skeleton-quantity" style="height: 45px; width: 120px;"></div>
            <div class="skeleton-button" style="height: 45px; width: 180px;"></div>
          </div>
        </div>
      </div>
    </div>

    <!-- Product Content - Se muestra inmediatamente cuando el producto está cargado -->
    <div v-else-if="product" class="product-details container d-flex justify-content-center pt-5 row col-12 pb-5 mt-5 pt-5 fade-in">
      <!-- Product Image -->
      <div class="col-lg-6 px-0 pe-lg-4 product-img-wrapper mt-5 d-flex flex-column align-items-center">
        <div 
          class="zoom-container mb-3"
          @mousemove="handleMouseMove"
          @mouseleave="handleMouseLeave"
          ref="zoomContainer"
        >
          <div class="zoom-overlay" v-show="isZoomed"></div>
          <img
            :src="selectedGalleryImage"
            :alt="product.name"
            class="zoom-image img-fluid"
            :class="{ 'zoomed': isZoomed }"
            :style="zoomStyle"
            ref="zoomImage"
          />
        </div>
        <div v-if="productGalleryImages.length > 1" class="product-gallery-thumbs">
          <button
            v-for="(img, index) in productGalleryImages"
            :key="`${img}-${index}`"
            type="button"
            class="product-gallery-thumb"
            :class="{ 'product-gallery-thumb--active': selectedGalleryImage === img }"
            @click="selectGalleryImage(img)"
            :aria-label="`Ver imagen ${index + 1}`"
          >
            <img :src="img" :alt="`${product.name} imagen ${index + 1}`" />
          </button>
        </div>
      </div>

      <!-- Product Info -->
      <div class="col-lg-5 my-4 my-lg-0 px-0 product-info pt-5">

        <!-- Admin Edit Button -->
        <router-link v-if="isAdmin" :to="`/configuracion/productos/editar/${product.id}`" class="btn btn-sm btn-outline-secondary mb-3">
          <i class="fas fa-edit me-2"></i>Editar Producto
        </router-link>
        <!-- Product Title -->
        <h1 class="product-title fw-bold text-white">{{ product.name }}</h1>

        <!-- Stock indicator -->
        <div class="stock-indicator mb-3">
          <span v-if="!product.is_available" class="stock-pill stock-unavailable">
            <i class="fas fa-times-circle me-1"></i>Sin stock
          </span>
          <span v-else-if="availableStock > 0 && availableStock <= 5" class="stock-pill stock-low">
            <i class="fas fa-exclamation-circle me-1"></i>Últimas {{ availableStock }} unidades
          </span>
          <span v-else-if="availableStock > 0" class="stock-pill stock-ok">
            <i class="fas fa-check-circle me-1"></i>En stock
          </span>
        </div>

        <!-- Description -->
        <div v-if="product.description" class="description-box mt-2 mb-4">
          <div class="product-description-preview text-white-50" v-html="truncatedDescription"></div>
          <button
            v-if="shouldShowReadMore"
            class="btn-read-more-link"
            @click="scrollToFullDescription"
          >
            Ver descripción completa <i class="fas fa-chevron-down ms-1"></i>
          </button>
        </div>
        <div v-else class="description-box mt-2 mb-4">
          <p class="text-white-50 mb-0">Sin descripción disponible.</p>
        </div>

        <!-- Divider -->
        <hr class="my-4 divider-light">

        <!-- Product Variants -->
        <div v-if="product.has_variants && product.variants && product.variants.length > 0" class="variants-section d-flex gap-3 mb-4">
          <div v-for="variant in product.variants" :key="variant.variant_name" class="variant-group mb-3 ">
            <label class="variant-label mb-2 w-100">
              <span class="fw-semibold text-white">{{ variant.variant_name }}</span>
              <span v-if="selectedVariants[variant.variant_name]" class="ms-2 text-primary fw-semibold">
                — {{ selectedVariants[variant.variant_name] }}
              </span>
            </label>
            <select
              class="form-select form-select-variant"
              :value="selectedVariants[variant.variant_name] || ''"
              @change="(e) => onVariantChange(variant, e.target.value)"
            >
              <option disabled value="">Seleccionar {{ variant.variant_name.toLowerCase() }}...</option>
              <option
                v-for="option in variant.options"
                :key="option.name"
                :value="option.name"
                :disabled="option.stock <= 0"
              >
                {{ option.name }}{{ option.stock <= 0 ? ' — Sin stock' : '' }}
              </option>
            </select>
          </div>
        </div>

        <!-- Price Section -->
        <div class="price-block mb-4">
          <div class="d-flex align-items-center gap-3 flex-wrap">
            <span v-if="showRegularPriceStrikethrough" class="price-original">
              {{ product.currency === 'USD' ? 'USD ' : '$' }}{{ formatDisplayPrice(strikethroughUnitPrice) }}
            </span>
            <span class="price-final">
              {{ product.currency === 'USD' ? 'USD ' : '$ ' }}{{ formatDiscountedPrice }}
            </span>
            <span v-if="isDistributorViewer" class="distributor-pill">
              Precio mayorista
            </span>
            <span v-if="displayDiscountPercent > 0" class="discount-pill">
              -{{ displayDiscountPercent }}% OFF
            </span>
          </div>
          <p v-if="displayDiscountPercent > 0 && unitSavingsPerUnit > 0" class="savings-note mt-1">
            <i class="fas fa-tag me-1"></i>
            Ahorrás {{ product.currency === 'USD' ? 'USD ' : '$' }}{{ formatDisplayPrice(unitSavingsPerUnit) }}
          </p>
        </div>

        <!-- Quantity + Cart Section -->
        <div class="cart-section">
          <!-- Quantity row -->
          <div class="d-flex align-items-center gap-2 mb-3">
            <span class="text-white-50 small me-1">Cantidad:</span>
            <div class="qty-control">
              <button class="qty-btn" @click="decrementQuantity" :disabled="quantity <= 1">
                <i class="fas fa-minus"></i>
              </button>
              <span class="qty-value">{{ quantity }}</span>
              <button class="qty-btn" @click="incrementQuantity"
                :disabled="quantity >= availableStock || product.is_available === false || !allVariantsSelected">
                <i class="fas fa-plus"></i>
              </button>
            </div>
            <span v-if="availableStock > 0" class="text-white-50 small ms-2">({{ availableStock }} disponibles)</span>
          </div>

          <!-- Action Buttons -->
          <div class="action-buttons d-flex gap-2">
            <button
              class="btn btn-buy-now"
              @click="buyNow"
              :disabled="isAddToCartDisabled"
            >
              <i class="fas fa-shopping-bag me-2"></i>
              {{ !allVariantsSelected && product.has_variants ? 'Selecciona una variante' : 'Comprar ahora' }}
            </button>
            <button
              class="btn btn-add-cart"
              @click="addToCart"
              :disabled="isAddToCartDisabled"
            >
              <i class="fas fa-shopping-cart me-2"></i>
              {{ !allVariantsSelected && product.has_variants ? 'Selecciona una variante' : 'Añadir al carrito' }}
            </button>
          </div>
        </div>

        <!-- Cotización de envío (Correo Argentino) -->
        <ShippingQuote
          v-if="product && shippingEnabled"
          :declared-value="productDeclaredValue"
          :weight="productWeight"
          :auto-select="false"
          start-expanded
        />

      </div>

      <div class="col-12 px-0 mt-4 product-tech-sheet">
        <ProductAttributes
          :attributes="product.attributes || {}"
          :definitions="attributeDefinitions"
        />
      </div>
    </div>

    <!-- No Product Found -->
    <div v-else class="container row col-12 d-flex justify-content-center mt-5 pt-5">
      <div class="text-center">
        <i class="fas fa-exclamation-circle fa-3x text-warning mb-3"></i>
        <h3 class="text-white">Producto no encontrado</h3>
        <p class="text-white-50">El producto que buscas no existe o no está disponible.</p>
        <router-link to="/productos" class="btn btn-primary mt-3">
          <i class="fas fa-arrow-left me-2"></i>
          Volver a Productos
        </router-link>
      </div>
    </div>
  </section>

  <!-- Full Description Section -->
  <section 
    v-if="product && product.description" 
    ref="fullDescriptionSection"
    class="container-fluid d-flex justify-content-center py-5 bg-dark full-description-section"
  >
    <div class="container px-lg-0">
      <div class="row justify-content-center">
        <div class="">
          <div class="full-description-card p-4">
            <h2 class="text-white fs-4 fw-bold mb-4 text-uppercase">
              <i class="fas fa-file-alt me-2"></i>
              Descripción completa
            </h2>
            <div class="full-description-content" v-html="product.description"></div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Related Products Section - Se muestra cuando el producto está cargado -->
  <section v-if="product" class="container-fluid d-flex justify-content-center py-5 bg-dark related-products-section">
    <div class="row bg-dark container">
      <div class="col-12">
        <h2 class="fw-bold display-6 text-center text-white text-uppercase mb-4">
          Productos relacionados
        </h2>
        
        <!-- Loading state for related products -->
        <div v-if="isLoadingRelated" class="related-products-loading">
          <div class="spinner"></div>
          <p class="text-white-50 mt-2">Cargando productos relacionados...</p>
        </div>
        
        <!-- Show products when loaded -->
        <ProductsCarousel 
          v-else-if="relatedProducts.length > 0"
          :products="relatedProducts" 
          :isLoading="false"
        />
        
        <!-- Empty state -->
        <div v-else class="text-center py-4">
          <p class="text-white-50">No hay productos relacionados disponibles</p>
        </div>
      </div>
    </div>
  </section>

</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { getProduct, getRelatedProducts, getProductAttributeDefinitions, getProducts } from "@/services/products";
import ProductAttributes from '@/components/ProductAttributes.vue';
import { formatPrice } from "@/helpers/product";
import ProductsCarousel from '@/components/ProductsCarousel.vue';
import ShippingQuote from '@/components/shipping/ShippingQuote.vue';
import { useStore } from 'vuex';
import { useToast } from "vue-toastification";
import { getShippingPublicConfig } from '@/services/shipping';

const route = useRoute();
const router = useRouter();
const store = useStore();
const toast = useToast();
const isAdmin = localStorage.getItem('isAdmin') === 'true';

// Variables manuales para los detalles del producto y estado de carga
const product = ref(null);
const isLoadingProduct = ref(true);
// Estado de variantes seleccionadas
const selectedVariants = ref({});

const quantity = ref(1);
const error = ref(null);

// Zoom functionality
const zoomContainer = ref(null);
const zoomImage = ref(null);
const zoomStyle = ref({});
const isZoomed = ref(false);
const selectedGalleryImage = ref('');

const FALLBACK_PRODUCT_IMAGE = require('@/assets/images/logo.png');

const productGalleryImages = computed(() => {
  if (!product.value) return [FALLBACK_PRODUCT_IMAGE];
  const raw = [
    product.value.image,
    product.value.image_2,
    product.value.image_3,
    product.value.image_4,
  ];
  const images = raw
    .map((v) => (typeof v === 'string' ? v.trim() : ''))
    .filter((v) => !!v);
  const unique = [...new Set(images)];
  return unique.length ? unique : [FALLBACK_PRODUCT_IMAGE];
});

// Estado de productos relacionados (simplificado)
const relatedProducts = ref([]);
const isLoadingRelated = ref(false);
const relatedProductsCache = ref(new Map());

// Full description section ref
const fullDescriptionSection = ref(null);

// Shipping
const shippingEnabled = ref(true);

/** Definiciones para etiquetas de ficha técnica (GET /product-attributes/) */
const attributeDefinitions = ref([]);

// Valor declarado para cotización (precio efectivo × cantidad)
const productDeclaredValue = computed(() => {
  if (!product.value) return 0;
  const opt = selectedPricingOption.value;
  const unitPrice = opt && opt.effective_price !== undefined && opt.effective_price !== null
    ? Number(opt.effective_price)
    : (product.value.effective_price !== undefined && product.value.effective_price !== null
      ? Number(product.value.effective_price)
      : price.value);
  return unitPrice * quantity.value;
});

// Peso del producto para cotización (gramos)
const productWeight = computed(() => {
  if (!product.value) return null;
  const opt = selectedPricingOption.value;
  if (opt && opt.weight && Number(opt.weight) > 0) return Number(opt.weight);
  if (product.value.weight && Number(product.value.weight) > 0) return Number(product.value.weight);
  return null;
});

// Description truncation settings
const DESCRIPTION_MAX_LENGTH = 200;

// Computed for truncated description
const truncatedDescription = computed(() => {
  if (!product.value?.description) return '';
  
  // Strip HTML tags for length calculation
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = product.value.description;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  
  if (textContent.length <= DESCRIPTION_MAX_LENGTH) {
    return product.value.description;
  }
  
  // Find a good breaking point
  let truncated = textContent.substring(0, DESCRIPTION_MAX_LENGTH);
  const lastSpace = truncated.lastIndexOf(' ');
  if (lastSpace > 0) {
    truncated = truncated.substring(0, lastSpace);
  }
  
  return truncated + '...';
});

// Should show read more button
const shouldShowReadMore = computed(() => {
  if (!product.value?.description) return false;
  
  const tempDiv = document.createElement('div');
  tempDiv.innerHTML = product.value.description;
  const textContent = tempDiv.textContent || tempDiv.innerText || '';
  
  return textContent.length > DESCRIPTION_MAX_LENGTH;
});

// Scroll to full description section
const scrollToFullDescription = () => {
  if (fullDescriptionSection.value) {
    fullDescriptionSection.value.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }
};


// Método para seleccionar variante
const selectVariant = (variantName, option) => {
  if (option.stock <= 0) return;
  selectedVariants.value[variantName] = option.name;
  if (quantity.value > availableStock.value) {
    quantity.value = availableStock.value;
  }
};

// Find the selected variant option that defines the price (uses backend’s pre-calculated effective_price)
const selectedPricingOption = computed(() => {
  if (!product.value?.has_variants || !product.value?.variants) return null;
  for (const variant of product.value.variants) {
    const selectedName = selectedVariants.value[variant.variant_name];
    if (!selectedName) continue;
    const option = variant.options.find(o => o.name === selectedName);
    if (option && option.effective_price !== undefined && option.effective_price !== null) {
      return option;
    }
  }
  // Fallback: any selected option with a price field
  for (const variant of product.value.variants) {
    const selectedName = selectedVariants.value[variant.variant_name];
    if (!selectedName) continue;
    const option = variant.options.find(o => o.name === selectedName);
    if (option && option.price !== undefined && option.price !== null) {
      return option;
    }
  }
  return null;
});

const isDistributorViewer = computed(() => {
  if (!product.value) return false;
  return store.getters.isDistributor || product.value.price_tier === 'DISTRIBUTOR';
});

const effectiveUnitPrice = computed(() => {
  if (!product.value) return 0;
  const opt = selectedPricingOption.value;
  if (opt && opt.effective_price !== undefined && opt.effective_price !== null) {
    return Number(opt.effective_price);
  }
  if (product.value.effective_price !== undefined && product.value.effective_price !== null) {
    return Number(product.value.effective_price);
  }
  return Number(price.value || 0);
});

const regularUnitPrice = computed(() => {
  if (!product.value) return 0;
  const opt = selectedPricingOption.value;
  if (opt && opt.price !== undefined && opt.price !== null) {
    return Number(opt.price);
  }
  return Number(product.value.price || 0);
});

// Base price before discount (for strikethrough display) — uses tier-resolved base_price
// injected by the backend serializer, so distributors see their tier's base price, not the
// regular price.
const price = computed(() => {
  if (!product.value) return 0;
  const opt = selectedPricingOption.value;
  if (opt) {
    // Prefer backend-injected base_price (tier-aware, before discount)
    if (opt.base_price !== undefined && opt.base_price !== null) return Number(opt.base_price);
    // Fallback: effective_price itself (no discount scenario or legacy data)
    if (opt.price !== undefined && opt.price !== null) return Number(opt.price);
  }
  const bp = product.value.base_price;
  if (bp !== undefined && bp !== null) return Number(bp);
  return Number(product.value.price || 0);
});

const strikethroughUnitPrice = computed(() => {
  if (!product.value) return 0;

  if (isDistributorViewer.value) {
    return regularUnitPrice.value;
  }

  if ((product.value.total_discount || product.value.discount) > 0) {
    return Number(price.value || 0);
  }

  return 0;
});

const showRegularPriceStrikethrough = computed(() => {
  if (!product.value) return false;
  const strike = Number(strikethroughUnitPrice.value || 0);
  if (!Number.isFinite(strike) || strike <= 0) return false;

  if (isDistributorViewer.value) {
    return strike > Number(effectiveUnitPrice.value || 0);
  }

  return (product.value.total_discount || product.value.discount) > 0;
});

// Add stock computation based on selected variants
const availableStock = computed(() => {
  if (!product.value) return 0;
  
  // If product has variants, calculate stock based on selected options
  if (product.value.has_variants && product.value.variants && product.value.variants.length > 0) {
    const stockValues = [];
    let allVariantsSelected = true;
    
    for (const variant of product.value.variants) {
      const selectedOption = selectedVariants.value[variant.variant_name];
      if (!selectedOption) {
        allVariantsSelected = false;
        continue;
      }
      
      const option = variant.options.find(opt => opt.name === selectedOption);
      if (option && option.stock !== undefined) {
        stockValues.push(option.stock);
      }
    }
    
    // If all variants are selected, return the minimum stock from selected options
    if (allVariantsSelected && stockValues.length > 0) {
      return Math.min(...stockValues);
    }
    
    // If no variants selected yet, calculate total available stock from all variant options
    // This allows adding to cart when any option has stock
    if (stockValues.length === 0) {
      let totalVariantStock = 0;
      for (const variant of product.value.variants) {
        if (variant.options && Array.isArray(variant.options)) {
          for (const option of variant.options) {
            if (option.stock !== undefined && option.stock > 0) {
              totalVariantStock += option.stock;
            }
          }
        }
      }
      // If there's stock in any variant option, return at least 1 to enable selection
      // The user still needs to select a variant to add to cart
      return totalVariantStock > 0 ? 1 : 0;
    }
    
    return stockValues.length > 0 ? Math.min(...stockValues) : 0;
  }
  
  // Default to product stock
  return product.value.stock;
});

// Computed to check if all required variants are selected
const allVariantsSelected = computed(() => {
  if (!product.value || !product.value.has_variants || !product.value.variants) {
    return true; // No variants required
  }
  
  if (product.value.variants.length === 0) {
    return true;
  }
  
  for (const variant of product.value.variants) {
    if (!selectedVariants.value[variant.variant_name]) {
      return false;
    }
  }
  return true;
});

// Computed to check if the add to cart button should be disabled
const isAddToCartDisabled = computed(() => {
  if (!product.value) return true;
  if (!product.value.is_available) return true;
  if (availableStock.value <= 0) return true;
  if (quantity.value > availableStock.value) return true;
  
  // If product has variants, all must be selected
  if (product.value.has_variants && product.value.variants && product.value.variants.length > 0) {
    if (!allVariantsSelected.value) return true;
    
    // Also check if selected variant has stock
    for (const variant of product.value.variants) {
      const selectedOption = selectedVariants.value[variant.variant_name];
      if (selectedOption) {
        const option = variant.options.find(opt => opt.name === selectedOption);
        // Option must exist and have stock > 0 (if stock is defined)
        // If stock is undefined, treat as available
        if (!option) {
          return true;
        }
        if (option.stock !== undefined && option.stock !== null && option.stock <= 0) {
          return true;
        }
      }
    }
  }
  
  return false;
});

const handleMouseMove = (e) => {
  if (!zoomContainer.value) return;
  isZoomed.value = true;
  const { left, top, width, height } = zoomContainer.value.getBoundingClientRect();
  const x = (e.clientX - left) / width;
  const y = (e.clientY - top) / height;

  zoomStyle.value = {
    transform: 'scale(2.5)',
    transformOrigin: `${x * 100}% ${y * 100}%`
  };
};

const handleMouseLeave = () => {
  isZoomed.value = false;
  zoomStyle.value = {
    transform: 'scale(1)',
    transformOrigin: 'center center'
  };
};

const selectGalleryImage = (imageUrl) => {
  selectedGalleryImage.value = imageUrl;
  handleMouseLeave();
};

watch(productGalleryImages, (images) => {
  if (!images || images.length === 0) {
    selectedGalleryImage.value = FALLBACK_PRODUCT_IMAGE;
    return;
  }
  if (!images.includes(selectedGalleryImage.value)) {
    selectedGalleryImage.value = images[0];
  }
}, { immediate: true });

// Manejar cambio desde select (dropdown)
const onVariantChange = (variant, optionName) => {
  const option = variant.options.find(o => o.name === optionName);
  if (!option) return;
  selectVariant(variant.variant_name, option);
};


// Format price with currency awareness: USD keeps 2 decimal places, ARS rounds to integer
const formatDisplayPrice = (amount) => {
  if (product.value?.currency === 'USD') {
    const num = Number(amount);
    if (isNaN(num)) return '0';
    return num.toFixed(2);
  }
  return formatPrice(amount);
};

// Effective price (after discount) — uses backend’s pre-calculated value
const formatDiscountedPrice = computed(() => {
  if (!product.value) return '0';
  const opt = selectedPricingOption.value;
  if (opt && opt.effective_price !== undefined && opt.effective_price !== null) {
    return formatDisplayPrice(Number(opt.effective_price));
  }
  if (product.value.effective_price !== undefined && product.value.effective_price !== null) {
    return formatDisplayPrice(Number(product.value.effective_price));
  }
  return formatDisplayPrice(price.value);
});

/** % mostrado (el backend puede mandar decimales largos). No parsear precios formateados tipo "26.000" con parseFloat. */
const displayDiscountPercent = computed(() => {
  if (!product.value) return 0
  const raw = Number(product.value.total_discount ?? product.value.discount ?? 0)
  if (!Number.isFinite(raw) || raw <= 0) return 0
  return Math.round(raw)
})

/** Ahorro por unidad: precio tachado (lista/base) − precio efectivo; nunca a partir de strings con separador de miles. */
const unitSavingsPerUnit = computed(() => {
  if (!product.value) return 0
  if (displayDiscountPercent.value <= 0) return 0
  const from = Number(strikethroughUnitPrice.value)
  const to = Number(effectiveUnitPrice.value)
  if (!Number.isFinite(from) || !Number.isFinite(to) || from <= 0) return 0
  return Math.max(0, from - to)
})

// Methods
const incrementQuantity = () => {
  if (quantity.value < availableStock.value) {
    quantity.value++;
  }
};

const decrementQuantity = () => {
  if (quantity.value > 1) {
    quantity.value--;
  }
};

const productId = ref(null);

// Función simplificada para normalizar productos relacionados
const normalizeRelatedProducts = (products) => {
  if (!Array.isArray(products)) return [];
  
  return products.map(product => {
    const normalizedProduct = { ...product };
    
    // Normalizar precio
    if (product.price && typeof product.price === 'object' && product.price.parsedValue !== undefined) {
      normalizedProduct.price = product.price.parsedValue;
    }
    
    // Normalizar descuento
    if (product.discount && typeof product.discount === 'object' && product.discount.parsedValue !== undefined) {
      normalizedProduct.discount = product.discount.parsedValue;
    }
    
    // Normalizar precio final
    if (product.final_price && typeof product.final_price === 'object' && product.final_price.parsedValue !== undefined) {
      normalizedProduct.final_price = product.final_price.parsedValue;
    }
    
    // Normalizar descuento total
    if (product.total_discount && typeof product.total_discount === 'object' && product.total_discount.parsedValue !== undefined) {
      normalizedProduct.total_discount = product.total_discount.parsedValue;
    }
    
    // Asegurar que variants sea un array
    if (product.variants === null) {
      normalizedProduct.variants = [];
    }
    
    return normalizedProduct;
  });
};

const extractProductsFromResponse = (response) => {
  if (!response) return [];
  if (Array.isArray(response)) return response;
  if (Array.isArray(response.results)) return response.results;
  if (Array.isArray(response.products)) return response.products;
  if (response.data) return extractProductsFromResponse(response.data);
  return [];
};

const loadFallbackProducts = async (currentProductId) => {
  const cacheKey = `fallback_${currentProductId}`;
  const cached = relatedProductsCache.value.get(cacheKey);

  if (cached && (Date.now() - cached.timestamp) < 300000) {
    return normalizeRelatedProducts(cached.data);
  }

  try {
    const response = await getProducts({ page_size: 24, is_available: true }, true);
    const products = extractProductsFromResponse(response);

    const filtered = products.filter(p => String(p.id) !== String(currentProductId)).slice(0, 12);
    const normalized = normalizeRelatedProducts(filtered);

    relatedProductsCache.value.set(cacheKey, {
      data: normalized,
      timestamp: Date.now()
    });

    return normalized;
  } catch (error) {
    console.error('Error loading fallback products:', error);
    return [];
  }
};

// Función simplificada para cargar productos relacionados
const loadRelatedProducts = async (currentProductId) => {
  // Verificar caché primero
  const requestedProductId = String(currentProductId);
  const cacheKey = `related_${currentProductId}`;
  const cached = relatedProductsCache.value.get(cacheKey);
  
  if (cached && (Date.now() - cached.timestamp) < 300000) { // 5 minutos de caché
    relatedProducts.value = normalizeRelatedProducts(cached.data);
    return;
  }

  try {
    isLoadingRelated.value = true;
    
    const response = await getRelatedProducts(currentProductId);
    
    let products = extractProductsFromResponse(response);
    
    // Normalizar y guardar en caché
    let normalizedProducts = normalizeRelatedProducts(products);
    if (normalizedProducts.length === 0) {
      normalizedProducts = await loadFallbackProducts(currentProductId);
    }

    relatedProductsCache.value.set(cacheKey, {
      data: normalizedProducts,
      timestamp: Date.now()
    });

    if (String(productId.value) !== requestedProductId) return;
    relatedProducts.value = normalizedProducts;
    
  } catch (error) {
    console.error('Error loading related products:', error);
    
    // En caso de error, usar caché aunque esté expirado
    if (cached) {
      if (String(productId.value) === requestedProductId) {
        relatedProducts.value = normalizeRelatedProducts(cached.data);
      }
    } else {
      const fallbackProducts = await loadFallbackProducts(currentProductId);
      if (String(productId.value) === requestedProductId) {
        relatedProducts.value = fallbackProducts;
      }
    }
  } finally {
    if (String(productId.value) === requestedProductId) {
      isLoadingRelated.value = false;
    }
  }
};

// Función optimizada para cargar el producto principal
const loadProduct = async () => {
  try {
    isLoadingProduct.value = true;
    error.value = null;
    selectedVariants.value = {};
    
    console.log("Cargando producto ID:", productId.value);
    
    // Cargar solo el producto principal primero
    const data = await getProduct(productId.value);
    
    if (!data) {
      throw new Error('Producto no encontrado');
    }
    
    console.log("Producto cargado:", data);
    product.value = data;

    // El producto principal ya llegó: mostrar el contenido YA.
    // Antes el skeleton esperaba también las definiciones de atributos
    // (otro request encadenado ~1.3s más), lo que hacía lenta la página.
    isLoadingProduct.value = false;

    // Avisar al loader fullscreen (solo aplica si entraste por URL directa)
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('app:initial-data-ready'));
    }

    // Cargar definiciones de atributos en segundo plano: la ficha técnica
    // (ProductAttributes al final de la página) se rellena cuando lleguen.
    getProductAttributeDefinitions(data.product_type || 'accessory')
      .then(defs => {
        attributeDefinitions.value = defs;
      })
      .catch(defErr => {
        console.warn('No se cargaron definiciones de atributos:', defErr);
        attributeDefinitions.value = [];
      });
    
    // Actualizar título del navegador con el nombre del producto
    document.title = `${data.name} | FIX GLASS`;
    
    // Inicializar variantes seleccionadas
    if (product.value.has_variants && product.value.variants && Array.isArray(product.value.variants)) {
      console.log("Inicializando variantes:", product.value.variants);
      
      for (const variant of product.value.variants) {
        if (variant.options && Array.isArray(variant.options) && variant.options.length > 0) {
          // Find first option with stock (treat undefined stock as available)
          const availableOption = variant.options.find(opt => {
            // If stock is not defined, treat as available
            if (opt.stock === undefined || opt.stock === null) return true;
            return opt.stock > 0;
          });
          
          if (availableOption) {
            selectedVariants.value[variant.variant_name] = availableOption.name;
            console.log(`Variante ${variant.variant_name} auto-seleccionada: ${availableOption.name}`);
          } else {
            // If no option with stock, select the first one anyway
            selectedVariants.value[variant.variant_name] = variant.options[0].name;
            console.log(`Variante ${variant.variant_name} sin stock, seleccionada primera: ${variant.options[0].name}`);
          }
        }
      }
      
      console.log("Variantes seleccionadas:", selectedVariants.value);
    }
    
    // Cargar productos relacionados desde caché si están disponibles
    const cachedRelated = relatedProductsCache.value.get(`related_${productId.value}`);
    if (cachedRelated) {
      relatedProducts.value = normalizeRelatedProducts(cachedRelated.data);
    }
    
  } catch (err) {
    console.error('Error loading product:', err);
    error.value = err.message || 'Error al cargar el producto';
    if (err.status === 404) {
      router.push('/404');
    }
  } finally {
    isLoadingProduct.value = false; // Ocultar skeleton inmediatamente
    
    // Cargar productos relacionados después de que el producto principal esté listo
    if (product.value) {
      loadRelatedProducts(productId.value);
    }
  }
};

// Watch para cambios de ID del producto
watch(() => route.params.id, (newId) => {
  console.log("ID del producto cambiado:", newId);
  productId.value = newId;
  
  // Resetear estado
  relatedProducts.value = [];
  
  loadProduct();
  window.scrollTo(0, 0);
}, { immediate: true });

// Setup
onMounted(async () => {
  // El título se actualiza dinámicamente en loadProduct()
  try {
    const shippingConfig = await getShippingPublicConfig();
    shippingEnabled.value = !!shippingConfig?.is_enabled;
  } catch (e) {
    shippingEnabled.value = false;
  }
});

// Función para agregar al carrito
const addToCart = () => {
  if (!product.value || availableStock.value <= 0) return;
  
  // Verify that all required variants are selected
  if (product.value.has_variants && product.value.variants && product.value.variants.length > 0) {
    const missingVariants = [];
    for (const variant of product.value.variants) {
      if (!selectedVariants.value[variant.variant_name]) {
        missingVariants.push(variant.variant_name);
      }
    }
    
    if (missingVariants.length > 0) {
      toast.warning(`Por favor selecciona: ${missingVariants.join(', ')}`);
      return;
    }
    
    // Verify that selected variant has stock
    let hasStock = true;
    for (const variant of product.value.variants) {
      const selectedOption = selectedVariants.value[variant.variant_name];
      const option = variant.options.find(opt => opt.name === selectedOption);
      // Option must exist, and if stock is defined, it must be > 0
      if (!option) {
        hasStock = false;
        break;
      }
      if (option.stock !== undefined && option.stock !== null && option.stock <= 0) {
        hasStock = false;
        break;
      }
    }
    
    if (!hasStock) {
      toast.error('La variante seleccionada no tiene stock disponible');
      return;
    }
  }

  // Get effective price from backend (already includes tier + discount + variant)
  const opt = selectedPricingOption.value;
  const effectivePrice = opt && opt.effective_price !== undefined && opt.effective_price !== null
    ? Number(opt.effective_price)
    : (product.value.effective_price !== undefined && product.value.effective_price !== null
      ? Number(product.value.effective_price)
      : price.value);

  const item = {
    id: product.value.id,
    name: product.value.name,
    price: effectivePrice,
    original_price: price.value,
    brand: product.value.brand,
    image: product.value.image,
    category_id: product.value.category_id,
    quantity: quantity.value,
    currency: product.value.currency || 'ARS',
    discount: product.value.total_discount || product.value.discount || 0,
    total_discount: product.value.total_discount || product.value.discount || 0,
    price_tier: product.value.price_tier || 'REGULAR',
    variants: product.value.has_variants ? { ...selectedVariants.value } : undefined,
    has_special_promotion: product.value.has_special_promotion || false,
    special_promotion_name: product.value.special_promotion_name || null,
  };

  store.dispatch('addToCart', item);
};

// Función para comprar ahora
const buyNow = () => {
  addToCart();
  router.push('/carrito');
};

</script>

<style scoped>
.tag {
  background-color: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.12);
  padding: 5px 12px;
  border-radius: 20px;
  font-size: 0.82rem;
  font-weight: 600;
  letter-spacing: 0.3px;
  transition: all 0.2s ease;
}

.tag:hover {
  transform: translateY(-1px);
  box-shadow: 0 3px 8px rgba(0, 0, 0, 0.12);
}

.tag-brand {
  background-color: rgba(255, 255, 255, 0.06);
  color: #ddd;
}

.product-details-section,
.full-description-section,
.related-products-section {
  background-color: #000;
}

/* Product title */
.product-title {
  font-size: clamp(1.6rem, 3vw, 3rem);
  line-height: 1.2;
  margin-bottom: 0.75rem;
  color: #fff;
  text-transform: uppercase;
}

/* Stock pill */
.stock-indicator { display: flex; align-items: center; }
.stock-pill {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  padding: 4px 12px;
  border-radius: 20px;
}
.stock-ok    { background: rgba(220, 38, 38, 0.2); color: #fecaca; }
.stock-low   { background: rgba(245, 158, 11, 0.18); color: #fcd34d; }
.stock-unavailable { background: rgba(239, 68, 68, 0.2); color: #fca5a5; }

/* Description box */
.description-box {
  background: rgba(255, 255, 255, 0.04);
  border-left: 3px solid var(--primary-color);
  border-radius: 0 10px 10px 0;
  padding: 0.9rem 1.1rem;
  font-size: clamp(0.95rem, 1.1vw, 1.05rem);
  line-height: 1.7;
  color: #d6d6d6;
}

.product-description-preview {
  font-size: clamp(0.95rem, 1.1vw, 1.05rem);
  line-height: 1.7;
}

.btn-read-more-link {
  background: none;
  border: none;
  color: var(--primary-color);
  font-size: 0.9rem;
  font-weight: 600;
  padding: 0;
  margin-top: 8px;
  cursor: pointer;
  transition: opacity 0.2s;
}
.btn-read-more-link:hover { opacity: 0.7; }

/* Info chip */
.info-chip {
  display: inline-flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.06);
  border-radius: 8px;
  padding: 6px 14px;
  font-size: 0.9rem;
  color: #e5e5e5;
}

.divider-light { border-color: rgba(255, 255, 255, 0.12); margin-left: 0; }

.product-tech-sheet {
  border-top: 1px solid rgba(255, 255, 255, 0.08);
  padding-top: 1.5rem;
}

/* Price block */
.price-block {
  padding: 1rem 1.25rem;
  background: rgba(255, 255, 255, 0.04);
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
}

.price-original {
  font-size: 1.05rem;
  color: #8b8b8b;
  text-decoration: line-through;
}

.price-final {
  font-size: 2.1rem;
  font-weight: 800;
  color: #fff;
  line-height: 1;
}

.discount-pill {
  background: rgba(239, 68, 68, 0.18);
  color: #fca5a5;
  font-size: 0.82rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(239, 68, 68, 0.35);
}

.distributor-pill {
  background: rgba(220, 38, 38, 0.2);
  color: #fecaca;
  font-size: 0.78rem;
  font-weight: 700;
  padding: 4px 10px;
  border-radius: 20px;
  border: 1px solid rgba(220, 38, 38, 0.35);
}

.savings-note {
  font-size: 0.82rem;
  color: #fca5a5;
  font-weight: 600;
  margin-bottom: 0;
}

/* Quantity control */
.qty-control {
  display: inline-flex;
  align-items: center;
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  border-radius: 10px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
}

.qty-btn {
  background: rgba(255, 255, 255, 0.06);
  border: none;
  width: 36px;
  height: 36px;
  font-size: 0.8rem;
  color: #d6d6d6;
  cursor: pointer;
  transition: background 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.qty-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.qty-btn:not(:disabled):hover { background: rgba(255, 255, 255, 0.12); }

.qty-value {
  min-width: 40px;
  text-align: center;
  font-size: 1rem;
  font-weight: 600;
  color: #fff;
}

/* Action buttons */
.action-buttons .btn { width: 100%; font-size: 1rem; font-weight: 600; padding: 0.85rem 1.5rem; border-radius: 12px; transition: all 0.25s; }

.btn-buy-now {
  background: var(--primary-color);
  color: #fff;
  border: 2px solid var(--primary-color);
}
.btn-buy-now:not(:disabled):hover {
  background: var(--primary-dark);
  border-color: var(--primary-dark);
  transform: translateY(-1px);
  box-shadow: 0 6px 18px rgba(0,0,0,0.18);
}
.btn-buy-now:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-add-cart {
  background: transparent;
  color: #fff;
  border: 2px solid rgba(255, 255, 255, 0.5);
}
.btn-add-cart:not(:disabled):hover {
  background: rgba(160, 160, 160, 0.08);
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  color: #fff;
}
.btn-add-cart:disabled { opacity: 0.5; cursor: not-allowed; }

/* Variant select */
.form-select-variant {
  border: 1.5px solid rgba(255, 255, 255, 0.18);
  border-radius: 10px;
  padding: 0.55rem 0.9rem;
  font-size: 0.95rem;
  background-color: #141414;
  color: #fff;
  transition: border-color 0.2s;
}
.form-select-variant:focus {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.25);
  outline: none;
}

/* Shipping info */
.shipping-info {
  border: 1.5px solid rgba(255, 255, 255, 0.12);
  border-radius: 14px;
  overflow: hidden;
}

.shipping-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
  padding: 0.9rem 1.1rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
  transition: background 0.2s;
}
.shipping-item:last-child { border-bottom: none; }
.shipping-item:hover { background: rgba(255, 255, 255, 0.04); }

.shipping-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.08);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fca5a5;
  font-size: 1rem;
  flex-shrink: 0;
  margin-top: 2px;
}

.shipping-title {
  font-size: 0.88rem;
  font-weight: 700;
  color: #fff;
  margin: 0;
  line-height: 1.3;
}

.shipping-desc {
  font-size: 0.81rem;
  color: #bdbdbd;
  margin: 2px 0 0;
  line-height: 1.4;
}

.product-img-wrapper {
  max-height: 800px;
  display: flex;
  transition: transform 0.3s ease;
  justify-content: center;
}

.zoom-container {
  position: relative;
  border-radius: 20px;
  background: linear-gradient(145deg, #0f0f0f, #1f1f1f);
  overflow: hidden;
  box-shadow: 0 10px 30px rgba(0,0,0,0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
}

.zoom-overlay {
  position: absolute;
  inset: 0;
  background: rgba(0,0,0,0.1);
  pointer-events: none;
  z-index: 1;
}

.zoom-image {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.zoom-image.zoomed {
  z-index: 2;
}

.product-gallery-thumbs {
  display: flex;
  gap: 0.55rem;
  margin-top: 1rem;
  width: 100%;
}

.product-gallery-thumb {
  border: 1px solid rgba(255, 255, 255, 0.22);
  border-radius: 12px;
  padding: 0;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.04);
  cursor: pointer;
  opacity: 0.75;
  transition: all 0.2s ease;
  aspect-ratio: 1;
  max-width: 80px;
}

.product-gallery-thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.product-gallery-thumb:hover {
  opacity: 1;
  transform: translateY(-1px);
}

.product-gallery-thumb--active {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(220, 38, 38, 0.3);
  opacity: 1;
}

/* Full Description Section */
.full-description-section {
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.full-description-card {
  background: #141414;
  border-radius: 20px;
  transition: all 0.3s ease;
}



.full-description-content {
  color: rgba(255, 255, 255, 0.85);
  font-size: clamp(0.95rem, 1.05vw, 1.05rem);
  line-height: 1.8;
}

.full-description-content :deep(p) {
  margin-bottom: 1rem;
}

.full-description-content :deep(ul),
.full-description-content :deep(ol) {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.full-description-content :deep(li) {
  margin-bottom: 0.5rem;
}

.full-description-content :deep(strong) {
  color: #fff;
  font-weight: 600;
}

.full-description-content :deep(em) {
  font-style: italic;
  color: rgba(255, 255, 255, 0.7);
}

.full-description-content :deep(h2),
.full-description-content :deep(h3) {
  color: #fff;
  font-weight: 700;
  margin-top: 1.5rem;
  margin-bottom: 0.75rem;
}

.full-description-content :deep(h2) {
  font-size: 1.2rem;
  border-bottom: 2px solid var(--primary-color);
  padding-bottom: 0.5rem;
}

.full-description-content :deep(h3) {
  font-size: 1.1rem;
  border-left: 3px solid var(--primary-color);
  padding-left: 0.75rem;
}

.full-description-content :deep(a) {
  color: var(--primary-color);
  text-decoration: underline;
}

.full-description-content :deep(a:hover) {
  color: var(--primary-color);
  text-decoration: none;
}

.product_description {
  font-size: clamp(0.95rem, 1.05vw, 1.05rem);
  color: #dcdcdc;
  line-height: 1.6;
}

.product_description :deep(p) {
  margin-bottom: 0.8rem;
}

.product_description :deep(ul) {
  padding-left: 1.5rem;
  margin-bottom: 0.8rem;
}

.product_description :deep(li) {
  margin-bottom: 0.4rem;
}

/* Product Description */
.product-description :deep(p) {
  margin-bottom: 0.5rem;
}

.product-description :deep(ul) {
  padding-left: 1.5rem;
}

/* Responsive design */
@media (max-width: 768px) {
  .product-details.container {
    flex-direction: column !important;
    padding: 0 !important;
  }
  .product-img-wrapper {
    max-width: 100%;
    margin-bottom: 1.5rem;
    padding: 0 0.5rem;
    justify-content: center;
  }
  .zoom-container {
    max-width: 95vw;
    min-height: 220px;
    margin: 0 auto;
  }
  .product-gallery-thumbs {
    grid-template-columns: repeat(4, minmax(0, 1fr));
  }
  .product-gallery-thumb img {
    height: 72px;
  }
  .product-info {
    padding: 0 0.5rem;
  }
  .product-title {
    font-size: 1.6rem;
  }
  .tags {
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
  }
  .price-block {
    padding: 0.75rem;
    margin-top: 1rem;
  }
  .price-final {
    font-size: 1.6rem;
  }
  .price-original {
    font-size: 0.95rem;
  }
  .action-buttons .btn {
    font-size: 0.95rem;
    padding: 0.75rem 1rem;
  }
  .shipping-info {
    margin-top: 1.5rem;
  }
  .full-description-card {
    padding: 1rem !important;
  }
  .full-description-content {
    font-size: 0.92rem;
    line-height: 1.6;
  }
  /* Desactivar zoom en mobile */
  .zoom-image.zoomed {
    transform: scale(1) !important;
  }
}
@media (max-width: 480px) {
  .zoom-container {
    max-width: 95vw;
    min-height: 160px;
  }
  .product-gallery-thumbs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
  .product-title {
    font-size: 1.3rem;
  }
  .price-block {
    padding: 0.5rem;
  }
  .cart-section {
    margin-top: 0.75rem;
  }
  .shipping-item {
    padding: 0.75rem;
  }
}

/* Enhanced animation */
.product-details {
  opacity: 0;
  animation: fadeIn 0.5s ease forwards;
}

@keyframes fadeIn {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

/* Related products section animations */
.related-products-trigger {
  opacity: 0;
  pointer-events: none;
}

/* Enhanced skeleton loading animation */
@keyframes loading {
  0% { background-position: 200% 0; opacity: 0.5; }
  50% { opacity: 1; }
  100% { background-position: -200% 0; opacity: 0.5; }
}

.skeleton-image,
.skeleton-title,
.skeleton-tag,
.skeleton-description,
.skeleton-price,
.skeleton-button,
.skeleton-variants,
.skeleton-quantity {
  background: linear-gradient(
    90deg,
    #1a1a1a 25%,
    #242424 50%,
    #1a1a1a 75%
  );
  background-size: 200% 100%;
  animation: loading 2s infinite ease-in-out;
  border-radius: 12px;
}

/* Spacing for right-side skeleton blocks */
.skeleton-content {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

/* Smooth transition for related products */
.related-products-section {
  transition: opacity 0.3s ease-in-out;
}

.related-products-section.loading {
  opacity: 0.7;
}

/* Optimized loading states */
.related-products-loading {
  min-height: 200px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.related-products-loading .spinner {
  width: 40px;
  height: 40px;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

</style>
